import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { endpoints } from "config";
import DeleteModal from "components/helpers/DeleteModal";

// Icons
import View from "assets/Icon_Action_01.png";
import Update from "assets/Icon_Action_02.png";
import Delete from "assets/Icon_Action_03.png";

import Category from "components/Category";
import { CrudItems } from "components/Category/CrudMenu";
import { lazyWithRetry } from "helpers/lazyWithRetry";

import AddBulk, { PAGES } from "pages/AddBulk/AddBulk";

import { CardListPage } from "components/Category/Category";
import { parseListDate } from "helpers/parseListDate";

const CatalogueSubGroupCustomType = lazyWithRetry(() =>
  import("./CatalogueSubGroupCustomType")
);

// Define the url for the table's data endpoint.
const url = endpoints.catalogueSubGroupCustomTypes.list;

export type Data = {
  catalogueItemSubGroups__Id: number;
  catalogueItemSubGroups__GroupName: string;
  catalogueItemSubGroups__bActive: boolean;
  catalogueItemSubGroups__AddDate: string;
  catalogueItemSubGroups__AddedBy: string;
  catalogueItemSubGroups__EditDate: string;
  catalogueItemSubGroups__LastEditedBy: string;
};

// Define which column on the table represents each row's ID.
// This should be unique to each row.
export const idColumn = "catalogueItemSubGroups__Id";

const crudItems: CrudItems<Data> = [
  {
    icon: View,
    title: "Create catalogue subgroup",
    imgAltText: "Create",
    menuCode: "CREATE",
    showInMultiSelect: false,
    permissionsKey: "create",
    content: (onDone, selectedData, menu) => (
      <CatalogueSubGroupCustomType
        selectedData={selectedData}
        onDone={onDone}
        idFromRowOption={menu.selectedId}
        actionType="create"
        submitButtonText="Add"
      />
    ),
    inRightPanel: true,
    isDisabled: false,
    notInMenus: true,
  },
  {
    icon: View,
    title: "View catalogue subgroup",
    imgAltText: "View",
    permissionsKey: "view",
    menuCode: "VIEW",
    showInMultiSelect: false,
    content: (
      onDone,
      selectedData,
      menu,
      additionalData,
      clickedIdIfMultiIds
    ) => (
      <CatalogueSubGroupCustomType
        onDone={onDone}
        selectedData={selectedData}
        idFromRowOption={menu.selectedId}
        actionType="view"
        submitButtonText="Close"
        hideActionButton={true}
        autoMenuActionsCode={"VIEW"}
        clickedIdIfMultiIds={clickedIdIfMultiIds}
      />
    ),
    inRightPanel: true,
    isDisabled: false,
  },
  {
    icon: Update,
    imgAltText: "Update",
    permissionsKey: "edit",
    title: "Update catalogue subgroup",
    menuCode: "UPDATE",
    showInMultiSelect: false,
    content: (onDone, selectedData, menu) => (
      <CatalogueSubGroupCustomType
        selectedData={selectedData}
        onDone={onDone}
        idFromRowOption={menu.selectedId}
        actionType="edit"
        submitButtonText="Update"
      />
    ),
    inRightPanel: true,
    isDisabled: false,
  },
  {
    icon: Delete,
    imgAltText: "Delete",
    permissionsKey: "delete",
    title: data => "Delete catalogue subgroup",
    menuCode: "DELETE",
    showInMultiSelect: false,
    content: (onActionComplete, selectedData, menu) => (
      <DeleteModal
        url={endpoints.catalogueSubGroupCustomTypes.delete}
        noun="catalogue subgroup"
        selectedIds={selectedData.map(d => d.catalogueItemSubGroups__Id)}
        selectedData={selectedData}
        open={menu.currentMenu === "DELETE"}
        close={onActionComplete}
        idFromRowOption={menu.selectedId}
        renderListItem={data => {
          return (
            <li>{`${
              data.catalogueItemSubGroups__Id
                ? `${data.catalogueItemSubGroups__Id}: `
                : ""
            }${
              data.catalogueItemSubGroups__GroupName
                ? ` ${data.catalogueItemSubGroups__GroupName}`
                : ""
            }`}</li>
          );
        }}
      />
    ),
    inRightPanel: false,
    isDisabled: false,
  },
];

// Optional - function for formatting a row by column name. Takes a row and should
// spit out an object with the same keys.
// Should do this for date columns and format them as required
const rowFormatter = (
  row: Data
): { [K in keyof typeof row]: React.ReactNode } => {
  let formattedRow: any = { ...row };

  if (formattedRow.catalogueItemSubGroups__AddDate) {
    formattedRow = {
      ...formattedRow,
      catalogueItemSubGroups__AddDate: parseListDate(
        formattedRow.catalogueItemSubGroups__AddDate,
        true
      ),
    };
  }

  if (formattedRow.catalogueItemSubGroups__EditDate) {
    formattedRow = {
      ...formattedRow,
      catalogueItemSubGroups__EditDate: parseListDate(
        formattedRow.catalogueItemSubGroups__EditDate,
        true
      ),
    };
  }

  return formattedRow;
};

export type DisableMenuItemData = {
  [key: string]: { fieldName: string; fieldValue: any };
};

export type Props = {};

const CatalogueSubGroupCustomTypes: React.FC = () => {
  const location = useLocation();
  return (
    <div className="CatalogueSubGroupCustomTypesPage CardListPage">
      <Category<Data>
        location={location}
        noun="catalogue subgroup"
        idColumn={idColumn}
        listEndpointUrl={url}
        hideCardPage={true}
        rowFormatter={rowFormatter}
        defaultBActiveFilterColumnKey={"CatalogueItemSubGroups__bActive"}
        crudComponents={crudItems}
        hiddenColumns={[idColumn]}
        disableMenuItemData={{
          resendverificationlink: {
            fieldName: "catalogueSubGroupUsers__bAccountVerified",
            fieldValue: true,
          },
        }}
        cardProps={{
          cardTitleColumn: "catalogueItemSubGroups__GroupName",
          cardTypeColumn: "catalogueItemSubGroups__Id",
          cardLayout: "CustomFields",
          cardActionType: "edit",
          includeTypeColumn: false,
          overwriteCardNoun: "catalogue subgroup",
          cardCustomContent: (data?: Data) => (
            <div className="InformationContainer">
              <div className="MainInfo"></div>
            </div>
          ),
          cardFooterButtonText: "Update custom types",
          cardContentClassname: "CardLayoutCustomFieldsContainer",
        }}
        hideHeaderTitle={true}
        largeRightPanel={false}
        rowCellOptions={{
          catalogueItemSubGroups__Name: { width: "Large2" },
          catalogueItemSubGroups__Description: { width: "Huge3" },
          catalogueItemSubGroups__OrderNumber: { width: "Huge" },
          catalogueItemSubGroups__bActive: { width: "Medium" },
          catalogueItemSubGroups__AddDate: { width: "SemiLarge" },
          catalogueItemSubGroups__AddedBy: { width: "SemiLarge2" },
          catalogueItemSubGroups__EditDate: { width: "SemiLarge" },
          catalogueItemSubGroups__LastEditedBy: { width: "SemiLarge2" },
        }}
      />
    </div>
  );
};

export default CatalogueSubGroupCustomTypes;
